import {
  BALANCE,
  PERMYRIAD,
  dockPaymentFactor,
  moduleStats,
  upgradeCheck,
  upgradeCost,
  upgradeTicks,
  type ModuleState,
  type StationState,
} from "@station-keeping/sim";
import { play } from "../../game/commands";
import { formatNumber, t } from "../../i18n";
import { errorLabel } from "../labels";
import { Icon } from "../pixel";
import { Key, Led, ScoreRow, SectionTitle } from "../primitives";
import { Price } from "./MaintenanceActions";

type GainKey = "power" | "battery" | "oxygen" | "crewCapacity" | "service" | "payment" | "speed" | "wear";

interface Gain {
  key: GainKey;
  from: string;
  to: string;
}

/** Part d'un bonus par niveau au-delà du 1, en pour cent. */
function percentAt(level: number, perLevel: number): number {
  return (Math.max(0, level - 1) * perLevel) / 100;
}

/** Ce que gagne un module en passant au niveau suivant : son effet propre, puis l'usure. */
function gains(module: ModuleState): Gain[] {
  const level = module.level;
  const now = moduleStats(module);
  const next = moduleStats({ type: module.type, level: level + 1 });
  const units = (hundredths: number) => formatNumber(hundredths / 100, 2);
  const list: Gain[] = [];
  switch (module.type) {
    case "solar_panel":
      list.push({ key: "power", from: formatNumber(now.power), to: formatNumber(next.power) });
      break;
    case "battery":
      list.push({ key: "battery", from: formatNumber(now.battery), to: formatNumber(next.battery) });
      break;
    case "o2_recycler":
      list.push({ key: "oxygen", from: units(now.oxygen), to: units(next.oxygen) });
      break;
    case "habitat":
      list.push({ key: "crewCapacity", from: formatNumber(now.crewCapacity), to: formatNumber(next.crewCapacity) });
      break;
    case "dock": {
      const bonus = (factor: number) => `+${formatNumber((factor - PERMYRIAD) / 100)} %`;
      list.push({ key: "payment", from: bonus(dockPaymentFactor(level)), to: bonus(dockPaymentFactor(level + 1)) });
      break;
    }
    case "maintenance_bay": {
      const per = BALANCE.upgrades.baySpeedPerLevel;
      list.push({ key: "speed", from: `${formatNumber(percentAt(level, per))} %`, to: `${formatNumber(percentAt(level + 1, per))} %` });
      break;
    }
    default:
      if (now.service > 0) list.push({ key: "service", from: units(now.service), to: units(next.service) });
  }
  const relief = BALANCE.upgrades.wearReliefPerLevel;
  list.push({ key: "wear", from: `${formatNumber(percentAt(level, relief))} %`, to: `${formatNumber(percentAt(level + 1, relief))} %` });
  return list;
}

/** Niveau d'un module en voyants : un par niveau, allumés jusqu'au sien. */
export function LevelLeds({ level }: { level: number }) {
  return (
    <span className="flex items-center gap-[var(--px)]" aria-hidden="true">
      {Array.from({ length: BALANCE.upgrades.maxLevel }, (_, index) => (
        <Led key={index} on={index < level} color="var(--color-accent)" />
      ))}
    </span>
  );
}

/**
 * L'amélioration d'un module : ce qu'elle apporte en chiffres, son prix et sa durée, ou ce qui l'empêche
 * (le niveau de la station, une panne, les crédits).
 */
export function UpgradeSection({ state, module }: { state: StationState; module: ModuleState }) {
  const max = BALANCE.upgrades.maxLevel;
  if (module.type === "core" || module.buildLeft > 0) return null;
  const code = upgradeCheck(state, module);
  const target = module.level + 1;

  return (
    <section aria-label={t("upgrade.title")}>
      <SectionTitle hint={module.level < max ? t("upgrade.hint") : undefined}>{t("upgrade.title")}</SectionTitle>
      {module.level >= max ? (
        <p className="text-[0.9375rem] text-muted">{t("upgrade.max")}</p>
      ) : (
        <>
          <dl>
            {gains(module).map((gain) => (
              <ScoreRow key={gain.key} label={t(`upgrade.gain.${gain.key}`)} value={`${gain.from} > ${gain.to}`} />
            ))}
          </dl>
          {code === "LEVEL_TOO_LOW" ? (
            <p className="mt-3 flex items-center gap-2 text-[0.9375rem] text-muted">
              <Icon name="level" className="text-accent" />
              {t("upgrade.needLevel", { level: target })}
            </p>
          ) : module.job?.kind === "upgrade" ? null : (
            <div className="mt-3 flex flex-col gap-1.5">
              <Key
                tone="action"
                className="w-full justify-between"
                disabled={code !== null}
                onClick={() => play({ type: "UPGRADE", moduleId: module.id })}
              >
                <span className="flex items-center gap-2">
                  <Icon name="upgrade" />
                  {t("upgrade.action", { level: target })}
                </span>
                <Price amount={upgradeCost(module)} seconds={upgradeTicks(state, module)} />
              </Key>
              {code !== null && code !== "NOT_ENOUGH_CREDITS" ? <p className="text-[0.875rem] text-warn">{errorLabel(code)}</p> : null}
            </div>
          )}
        </>
      )}
    </section>
  );
}
